import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector, useHistory, useUpdateQueryParams } from 'src/utils/Hooks';
import { useDispatch } from 'react-redux';
import {
  buildCustomDateRangeDisplayName,
  computeComparisonTimePeriod,
  getDayIdFromDate
} from 'src/utils/dateformatting';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import { updateAllTimePeriods, updateMainTimePeriod } from 'src/store/modules/main-time-period/operations';
import { History } from 'history';
import { getStartAndEndOfWeek, getWeekId } from 'src/utils/dateUtils';
import AppCalendarContainer from 'src/components/BeaconV2Components/common/SlCalendarPicker/AppCalendarContainer';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { TimePeriod } from 'src/types/store/storeTypes';
import moment from 'moment';
import { getSearchAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector';
import { useBeaconRoutes, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';

interface TimePeriodDropdownProps {
  allowedTimePeriods?: (timePeriod: TimePeriod) => boolean;
  placement?: string;
}

export const BEACON_PRO_TIME_PERIODS = {
  LAST_WEEK: '1w',
  MTD: 'mtd',
  LAST_FOUR_WEEKS: '4w',
  LAST_THIRTEEN_WEEKS: '13w',
  LAST_TWENTY_SIX_WEEKS: '26w',
  LAST_FIFTY_TWO_WEEKS: '52w',
  YEAR_TO_DATE: 'ytd',
  LAST_YEAR: '1y',
  CUSTOM: 'cd'
};

export const getAtlasBeaconTimePeriods = (t: TimePeriod) => {
  return t.id !== BEACON_PRO_TIME_PERIODS.MTD;
};

/**
 * Hook which returns a function used to update the main and comparison time periods and dispatch actions for updating Redux state and query parameters.
 *
 * @param {Object} options - Options object containing the history object.
 * @param {History} options.history - The history object from react-router-dom.
 * @returns {Function} - A function to update time periods.
 */
export const useUpdateTimePeriods = ({ history }: { history: History }) => {
  const retailer = useAppSelector((state) => state.retailer);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const allWeekIdsByRetailerId = useAppSelector((state) => state.allWeekIdsByRetailerId);
  const weekIdCollection = allWeekIdsByRetailerId[retailer.id];
  const dispatch = useDispatch();
  const updateQuery = useUpdateQueryParams(history);

  const { id: comparisonTimeWindow } = _cloneDeep(comparisonTimePeriod);

  return (timePeriod: TimePeriod) => {
    const updatedMainPeriod = {
      ...timePeriod,
      startWeek: timePeriod.startWeek,
      endWeek: timePeriod.endWeek,
      startDayId: timePeriod.startDayId,
      endDayId: timePeriod.endDayId,
      startWeekStartDate: timePeriod.startWeekStartDate,
      endWeekEndDate: timePeriod.endWeekEndDate,
      id: timePeriod.id,
      shortDisplayName: timePeriod.shortDisplayName ?? timePeriod.displayName
    };

    const updatedComparisonPeriod = computeComparisonTimePeriod(
      weekIdCollection,
      updatedMainPeriod,
      comparisonTimeWindow
    );

    updatedComparisonPeriod.availableComparisonTimePeriods = comparisonTimePeriod.availableComparisonTimePeriods;
    updatedComparisonPeriod.comparisonIndex = comparisonTimePeriod.availableComparisonTimePeriods
      .map((x) => x.id)
      .indexOf(comparisonTimePeriod.id);

    dispatch(updateAllTimePeriods(weekIdCollection, updatedMainPeriod, updatedComparisonPeriod));
    updateQuery({ updatedMainPeriod, updatedComparisonPeriod });
  };
};

const TimePeriodDropdown = ({ allowedTimePeriods = () => true, placement }: TimePeriodDropdownProps) => {
  const history = useHistory();
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const retailer = useAppSelector((state) => state.retailer);
  const allWeekIdsByRetailerId = useAppSelector((state) => state.allWeekIdsByRetailerId);
  const anchorEl = useRef<HTMLDivElement>(null);
  const { currentRoute } = useBeaconRoutes();

  const [selectedPeriodId, setSelectedPeriodId] = useState(mainTimePeriod.id);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const app = useAppSelector((state) => state.app);
  const dataFrequency = getSearchAppName() === AppName.Advertising ? 'daily' : app.dataFrequency;

  const dispatch = useDispatch();
  const updateQuery = useUpdateQueryParams(history);

  const timePeriods = mainTimePeriod.availableMainTimePeriods;
  const updateTimePeriods = useUpdateTimePeriods({ history });

  const selectedTimePeriod = useMemo(() => {
    return (
      timePeriods.find((period) => period.id === selectedPeriodId) ||
      timePeriods.find((period) => _get(period, 'default') === true)
    );
  }, [selectedPeriodId, timePeriods]);

  useEffect(() => {
    const { availableMainTimePeriods } = mainTimePeriod;
    const selectedOption = _cloneDeep(
      availableMainTimePeriods.filter((val) => val.id === mainTimePeriod.id)[0] || availableMainTimePeriods[4]
    );

    if (selectedOption.id === 'cd') {
      const shortDisplayName = buildCustomDateRangeDisplayName(mainTimePeriod.startDayId, mainTimePeriod.endDayId);
      dispatch(
        updateMainTimePeriod(
          mainTimePeriod.startWeek,
          mainTimePeriod.endWeek,
          selectedOption.id,
          shortDisplayName,
          availableMainTimePeriods,
          mainTimePeriod.startWeekStartDate,
          mainTimePeriod.startDayId,
          mainTimePeriod.endWeekEndDate,
          mainTimePeriod.endDayId
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedPeriodId(mainTimePeriod.id);
  }, [mainTimePeriod.id]);

  const handleDateRangeChange = (periodId: string) => {
    if (periodId === 'cd') {
      setIsCalendarOpen(true);
    } else {
      setSelectedPeriodId(periodId);
      const newTimePeriod = timePeriods.find((period) => period.id === periodId);
      updateTimePeriods(newTimePeriod);
    }
  };

  const handleOutsideClick = () => {
    setIsCalendarOpen(false);
  };

  const updateWeeks = (
    mainStartWeek: number,
    mainEndWeek: number,
    mainId: string,
    shortDisplayName: string | null,
    isCustomDate: boolean,
    startDayId: number,
    endDayId: number,
    startWeekStartDate: string,
    endWeekEndDate: string
  ) => {
    const allWeekIds = allWeekIdsByRetailerId[retailer.id];
    const { id: comparisonTimeWindow } = _cloneDeep(comparisonTimePeriod);
    let updatedMainPeriodDisplayName = shortDisplayName;
    if (isCustomDate) {
      updatedMainPeriodDisplayName = buildCustomDateRangeDisplayName(startDayId, endDayId);
      // You cannot use `setState` in a functional component. Instead, handle the state using hooks.
      // For this specific case, you can use another state variable for 'isCalendarOpen' and set it to 'false' when needed.
    }

    const maxWeekId = Math.max(...allWeekIds);

    const updatedMainPeriod = {
      startWeek: Math.min(mainStartWeek, maxWeekId),
      endWeek: Math.min(mainEndWeek, maxWeekId),
      startDayId,
      endDayId,
      startWeekStartDate,
      endWeekEndDate,
      id: mainId,
      shortDisplayName: updatedMainPeriodDisplayName
    };

    const updatedComparisonPeriod = computeComparisonTimePeriod(allWeekIds, updatedMainPeriod, comparisonTimeWindow);
    updatedComparisonPeriod.availableComparisonTimePeriods = comparisonTimePeriod.availableComparisonTimePeriods;
    updatedComparisonPeriod.comparisonIndex = comparisonTimePeriod.availableComparisonTimePeriods
      .map((x) => x.id)
      .indexOf(comparisonTimePeriod.id);

    dispatch(updateAllTimePeriods(allWeekIds, updatedMainPeriod, updatedComparisonPeriod));

    updateQuery({ updatedMainPeriod, updatedComparisonPeriod });
  };

  const handleCustomDateRangeChange = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
    if (startDate === null || endDate === null) {
      return;
    }

    const startWeekId = getWeekId(startDate);
    const endWeekId = getWeekId(endDate);
    let { startDate: startWeekStartDate } = getStartAndEndOfWeek(startWeekId);
    let { endDate: endWeekEndDate } = getStartAndEndOfWeek(endWeekId);
    if (dataFrequency === 'daily') {
      startWeekStartDate = moment(startDate).format('YYYYMMDD');
      endWeekEndDate = moment(endDate).format('YYYYMMDD');
    }

    updateWeeks(
      startWeekId,
      endWeekId,
      'cd',
      null,
      true,
      getDayIdFromDate(startWeekStartDate),
      getDayIdFromDate(endWeekEndDate),
      startWeekStartDate,
      endWeekEndDate
    );
    handleOutsideClick();
  };

  const timePeriodOptions = timePeriods.filter(allowedTimePeriods).map((period) => {
    return { id: period.id, label: period.shortDisplayName };
  });

  return (
    <>
      <SlDropdownMenu
        ref={anchorEl}
        onChange={(period) => handleDateRangeChange(period.id)}
        defaultLabel={selectedTimePeriod.shortDisplayName}
        selectedId={selectedTimePeriod.id}
        options={timePeriodOptions}
      />
      {isCalendarOpen && (
        <AppCalendarContainer
          handleCustomDateRangeChange={handleCustomDateRangeChange}
          open={isCalendarOpen}
          setCalendarOpen={setIsCalendarOpen}
          anchorEl={anchorEl.current}
          placement={placement}
        />
      )}
    </>
  );
};

export default memo(TimePeriodDropdown);
